import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { ApplyStepper } from "@/components/apply/ApplyStepper";
import { ApplyTimeline } from "@/components/apply/ApplyTimeline";
import { BackLink } from "@/components/brand/BackLink";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { US_STATES, formatPhone } from "@/lib/us-states";
import { getOrCreateSessionId } from "@/lib/academy-session";
import {
  loadDraftBySession,
  submitApplication,
  upsertDraft,
} from "@/lib/apply.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/careers/apply")({
  head: () => ({
    meta: [
      { title: "Become a Currency Thoughts Strategist — Apply" },
      {
        name: "description",
        content:
          "A three-step strategist application. We respond personally, not algorithmically.",
      },
      { property: "og:title", content: "Become a Currency Thoughts Strategist" },
      {
        property: "og:description",
        content:
          "Apply to join a veteran-led, family-first independent insurance agency.",
      },
    ],
  }),
  component: ApplyPage,
});

type FormState = {
  full_name: string;
  email: string;
  phone: string;
  state: string;
  heard_about: string;
  license_status: string;
  license_lines: string[];
  years_experience: string;
  current_situation: string;
  income_target: string;
  why_currency_thoughts: string;
  success_vision: string;
  additional_notes: string;
  consent_contractor: boolean;
  consent_discovery_call: boolean;
};

const EMPTY: FormState = {
  full_name: "",
  email: "",
  phone: "",
  state: "",
  heard_about: "",
  license_status: "",
  license_lines: [],
  years_experience: "",
  current_situation: "",
  income_target: "",
  why_currency_thoughts: "",
  success_vision: "",
  additional_notes: "",
  consent_contractor: false,
  consent_discovery_call: false,
};

const STEP_TITLES: Record<1 | 2 | 3, string> = {
  1: "Tell us about you",
  2: "Your background",
  3: "Your fit",
};

const HEARD_OPTIONS = [
  { v: "friend-family", l: "Friend / Family" },
  { v: "social-media", l: "Social Media" },
  { v: "search", l: "Search" },
  { v: "podcast", l: "Podcast" },
  { v: "ct-event", l: "Currency Thoughts Event" },
  { v: "other", l: "Other" },
];

const LICENSE_OPTIONS = [
  { v: "life", l: "Yes — Life" },
  { v: "life-health", l: "Yes — Life + Health" },
  { v: "in-process", l: "In process" },
  { v: "not-yet", l: "Not yet" },
];

const YEARS_OPTIONS = ["0", "1-2", "3-5", "6-10", "10+"];

const SITUATION_OPTIONS = [
  { v: "full-time-switch", l: "Full-time agent looking to switch" },
  { v: "part-time", l: "Part-time agent" },
  { v: "career-changer", l: "Career changer" },
  { v: "recent-grad", l: "Recent graduate" },
  { v: "returning", l: "Returning to workforce" },
];

const INCOME_OPTIONS = [
  { v: "3-5k", l: "$3-5k / month" },
  { v: "5-10k", l: "$5-10k / month" },
  { v: "10-20k", l: "$10-20k / month" },
  { v: "20k+", l: "$20k+ / month" },
  { v: "not-focused", l: "Not focused on income" },
];

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="font-sans text-[12px] font-semibold uppercase tracking-[0.14em] text-charcoal-900">
      {children}
      {required && <span className="text-bronze-600 ml-1">*</span>}
    </Label>
  );
}

function ApplyPage() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string>("");
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upsert = useServerFn(upsertDraft);
  const loadDraft = useServerFn(loadDraftBySession);
  const submit = useServerFn(submitApplication);

  // Init session + fit score
  useEffect(() => {
    const sid = getOrCreateSessionId();
    setSessionId(sid);
    loadDraft({ data: { session_id: sid } })
      .then((res) => {
        if (res.draft) {
          setForm((p) => ({ ...p, ...res.draft, license_lines: res.draft.license_lines ?? [] }));
          if (res.draft.step_completed >= 2) setStep(3);
          else if (res.draft.step_completed >= 1) setStep(2);
        }
      })
      .finally(() => setHydrated(true));
  }, [loadDraft]);

  // Debounced autosave on form change
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!hydrated || !sessionId || !form.email) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      setSaving(true);
      const fitScoreRaw =
        typeof window !== "undefined" ? window.localStorage.getItem("ct_fit_score") : null;
      const fit_score = fitScoreRaw ? Number(fitScoreRaw) : undefined;
      upsert({
        data: {
          session_id: sessionId,
          step: step - 1,
          payload: { ...form, ...(fit_score ? { fit_score } : {}) },
        },
      })
        .catch(() => {})
        .finally(() => setSaving(false));
    }, 1000);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [form, hydrated, sessionId, step, upsert]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  function validateStep(s: 1 | 2 | 3): string | null {
    if (s === 1) {
      if (!form.full_name.trim()) return "Full name is required.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Valid email is required.";
      if (form.phone.replace(/\D/g, "").length < 10) return "Valid phone is required.";
      if (!form.state) return "State is required.";
      if (!form.heard_about) return "Please tell us how you heard about us.";
    }
    if (s === 2) {
      if (!form.license_status) return "Please select your license status.";
      if (!form.years_experience) return "Please select your years of experience.";
      if (!form.current_situation) return "Please select your current situation.";
      if (!form.income_target) return "Please select your income target.";
    }
    if (s === 3) {
      if (form.why_currency_thoughts.trim().length < 30)
        return "Please share at least 30 characters about why Currency Thoughts.";
      if (form.success_vision.trim().length < 30)
        return "Please share at least 30 characters about your 12-month vision.";
      if (!form.consent_contractor) return "Please acknowledge the contractor terms.";
      if (!form.consent_discovery_call) return "Please consent to a discovery call.";
    }
    return null;
  }

  async function handleContinue() {
    const err = validateStep(step);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    const fitScoreRaw =
      typeof window !== "undefined" ? window.localStorage.getItem("ct_fit_score") : null;
    const fit_score = fitScoreRaw ? Number(fitScoreRaw) : undefined;
    try {
      const res = await upsert({
        data: {
          session_id: sessionId,
          step,
          payload: { ...form, ...(fit_score ? { fit_score } : {}) },
        },
      });
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      if (step < 3) {
        setStep((step + 1) as 1 | 2 | 3);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setSubmitting(true);
        const final = await submit({ data: { session_id: sessionId } });
        if (!final.ok) {
          toast.error(final.error);
          setSubmitting(false);
          return;
        }
        trackEvent("advisor_application_submitted");
        navigate({
          to: "/careers/apply/confirmation",
          search: { id: final.token },
        });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="bg-cream-50 py-16 md:py-20 min-h-screen">
      <div className="container mx-auto px-6 md:px-10">
        <BackLink to="/careers" label="Back to careers" className="mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-12 lg:gap-16 items-start">
          {/* Form card */}
          <div
            className="bg-white p-7 md:p-10"
            style={{
              borderRadius: "4px",
              borderTop: "3px solid var(--color-bronze-600, #A88B5C)",
              boxShadow:
                "0 10px 40px -10px rgba(168,139,92,0.18), 0 2px 8px rgba(168,139,92,0.08)",
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <ApplyStepper step={step} />
              <span className="font-sans text-[11px] text-charcoal-500">
                {saving ? "Saving…" : hydrated ? "Saved" : ""}
              </span>
            </div>

            <Eyebrow>Step {step} of 3</Eyebrow>
            <h2 className="font-serif text-3xl md:text-[36px] text-navy-900 mt-4 leading-tight">
              {STEP_TITLES[step]}
            </h2>

            <div className="mt-8 space-y-6">
              {step === 1 && <Step1 form={form} set={set} />}
              {step === 2 && <Step2 form={form} set={set} />}
              {step === 3 && <Step3 form={form} set={set} />}
            </div>

            <div className="mt-10 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => step > 1 && setStep((step - 1) as 1 | 2 | 3)}
                disabled={step === 1}
                className={cn(
                  "font-sans text-[13px] uppercase tracking-[0.05em] text-charcoal-500",
                  step === 1 ? "opacity-40 cursor-not-allowed" : "hover:text-bronze-600",
                )}
              >
                ← Back
              </button>
              <PrimaryButton onClick={handleContinue} disabled={submitting}>
                {step === 3
                  ? submitting
                    ? "Submitting…"
                    : "Submit Application →"
                  : "Continue →"}
              </PrimaryButton>
            </div>
          </div>

          <ApplyTimeline />
        </div>
      </div>
    </main>
  );
}

function Step1({
  form,
  set,
}: {
  form: FormState;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}) {
  return (
    <>
      <div className="space-y-2">
        <FieldLabel required>Full name</FieldLabel>
        <Input
          value={form.full_name}
          onChange={(e) => set("full_name", e.target.value)}
          placeholder="Jane Doe"
          maxLength={120}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <FieldLabel required>Email</FieldLabel>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@example.com"
            maxLength={255}
          />
        </div>
        <div className="space-y-2">
          <FieldLabel required>Phone</FieldLabel>
          <Input
            value={form.phone}
            onChange={(e) => set("phone", formatPhone(e.target.value))}
            placeholder="+1 (555) 555-5555"
            inputMode="tel"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <FieldLabel required>State of residence</FieldLabel>
          <Select value={form.state} onValueChange={(v) => set("state", v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <FieldLabel required>How did you hear about us?</FieldLabel>
          <Select
            value={form.heard_about}
            onValueChange={(v) => set("heard_about", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent>
              {HEARD_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v}>
                  {o.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
}

function Step2({
  form,
  set,
}: {
  form: FormState;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}) {
  const showLicenseStates =
    form.license_status === "life" ||
    form.license_status === "life-health" ||
    form.license_status === "in-process";

  const toggleLicenseState = (code: string) => {
    const next = form.license_lines.includes(code)
      ? form.license_lines.filter((c) => c !== code)
      : [...form.license_lines, code];
    set("license_lines", next);
  };

  return (
    <>
      <div className="space-y-3">
        <FieldLabel required>Are you currently licensed?</FieldLabel>
        <RadioGroup
          value={form.license_status}
          onValueChange={(v) => set("license_status", v)}
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          {LICENSE_OPTIONS.map((o) => (
            <label
              key={o.v}
              className={cn(
                "flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors",
                form.license_status === o.v
                  ? "border-bronze-600 bg-bronze-600/5"
                  : "border-charcoal-500/20 hover:border-bronze-600/60",
              )}
              style={{ borderRadius: "4px" }}
            >
              <RadioGroupItem value={o.v} />
              <span className="font-sans text-[14px] text-charcoal-900">{o.l}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      {showLicenseStates && (
        <div className="space-y-3">
          <FieldLabel>License states (select all that apply)</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {US_STATES.map((s) => {
              const active = form.license_lines.includes(s.code);
              return (
                <button
                  type="button"
                  key={s.code}
                  onClick={() => toggleLicenseState(s.code)}
                  className={cn(
                    "px-3 py-1.5 text-[12px] font-sans font-semibold border transition-colors",
                    active
                      ? "border-bronze-600 bg-bronze-600 text-navy-900"
                      : "border-charcoal-500/30 text-charcoal-900 hover:border-bronze-600",
                  )}
                  style={{ borderRadius: "2px" }}
                >
                  {s.code}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <FieldLabel required>Years in insurance / financial services</FieldLabel>
          <Select
            value={form.years_experience}
            onValueChange={(v) => set("years_experience", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {YEARS_OPTIONS.map((y) => (
                <SelectItem key={y} value={y}>
                  {y} {y === "0" ? "(none)" : "years"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <FieldLabel required>Monthly income target</FieldLabel>
          <Select
            value={form.income_target}
            onValueChange={(v) => set("income_target", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {INCOME_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v}>
                  {o.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        <FieldLabel required>Current situation</FieldLabel>
        <RadioGroup
          value={form.current_situation}
          onValueChange={(v) => set("current_situation", v)}
          className="grid grid-cols-1 gap-2"
        >
          {SITUATION_OPTIONS.map((o) => (
            <label
              key={o.v}
              className={cn(
                "flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors",
                form.current_situation === o.v
                  ? "border-bronze-600 bg-bronze-600/5"
                  : "border-charcoal-500/20 hover:border-bronze-600/60",
              )}
              style={{ borderRadius: "4px" }}
            >
              <RadioGroupItem value={o.v} />
              <span className="font-sans text-[14px] text-charcoal-900">{o.l}</span>
            </label>
          ))}
        </RadioGroup>
      </div>
    </>
  );
}

function CharCount({ value, max, min }: { value: string; max: number; min?: number }) {
  const len = value.trim().length;
  const under = min !== undefined && len < min;
  const over = len > max;
  return (
    <p
      className={cn(
        "font-sans text-[11px] mt-1 text-right tabular-nums",
        over ? "text-danger" : under ? "text-charcoal-500" : "text-bronze-600",
      )}
    >
      {len} / {max}
    </p>
  );
}

function Step3({
  form,
  set,
}: {
  form: FormState;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}) {
  return (
    <>
      <div className="space-y-2">
        <FieldLabel required>Why Currency Thoughts specifically?</FieldLabel>
        <Textarea
          rows={4}
          maxLength={500}
          value={form.why_currency_thoughts}
          onChange={(e) => set("why_currency_thoughts", e.target.value)}
          placeholder="What drew you to us — in your own words."
        />
        <CharCount value={form.why_currency_thoughts} max={500} min={30} />
      </div>
      <div className="space-y-2">
        <FieldLabel required>What does success look like 12 months from now?</FieldLabel>
        <Textarea
          rows={4}
          maxLength={500}
          value={form.success_vision}
          onChange={(e) => set("success_vision", e.target.value)}
          placeholder="Be specific. We hold the people we hire to their own vision."
        />
        <CharCount value={form.success_vision} max={500} min={30} />
      </div>
      <div className="space-y-2">
        <FieldLabel>Anything else we should know? (optional)</FieldLabel>
        <Textarea
          rows={3}
          maxLength={500}
          value={form.additional_notes}
          onChange={(e) => set("additional_notes", e.target.value)}
        />
        <CharCount value={form.additional_notes} max={500} />
      </div>

      <div className="space-y-4 pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={form.consent_contractor}
            onCheckedChange={(v) => set("consent_contractor", v === true)}
            className="mt-0.5"
          />
          <span className="font-sans text-[13px] leading-[1.55] text-charcoal-900">
            I understand this is an independent contractor opportunity, not salaried employment.
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={form.consent_discovery_call}
            onCheckedChange={(v) => set("consent_discovery_call", v === true)}
            className="mt-0.5"
          />
          <span className="font-sans text-[13px] leading-[1.55] text-charcoal-900">
            I consent to a discovery call within 48 hours.
          </span>
        </label>
      </div>
    </>
  );
}